/* eslint-disable no-useless-escape */

/* eslint-disable no-labels */
import React, { useEffect, useState, useRef } from 'react';
import PrimaryLoader from '../../../atoms/primaryLoader/PrimaryLoader';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import YupPassword from 'yup-password';
import { useCartContext } from '../../../../provider/cart/CartContext';

import Button from '../../../atoms/button';
import { TextField } from '../../../atoms/textField/TextField';
import { giftCardSchema } from '../utils/validationSchemas';
import styles from './GiftCardForm.module.scss';
import { options } from './options';
import { Heading } from '../../../typography/heading/Heading';
import clsx from 'clsx';
import TextSwatches from '../../textSwatches/TextSwatches';
import textSwatchesStyles from '../../textSwatches/TextSwatches.module.scss';
import { useYupValidationResolver } from '../../../../utils/useYupValidationResolver';

YupPassword(yup);

const getValue = (price) => '$' + price / 100;

const GiftCardForm = ({ variants, setActiveVariant, handleAddToCart, activeVariant, productId }) => {
  const resolver = useYupValidationResolver(giftCardSchema);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    clearErrors,

    formState: { errors },
  } = useForm({
    resolver,
    mode: 'onBlur',
  });

  const { setCartData } = useCartContext();
  const watchCustomAmount = watch('customAmount');
  const watchAmount = watch('amount');
  const watchMessageToRecipient = watch('messageToRecipient');

  const textareaRef = useRef(null);

  const [iconStatus, setIconStatus] = useState(false);
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    const cancelScrollForInput = () => {
      if (document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    };

    document.addEventListener('wheel', cancelScrollForInput);

    return () => document.removeEventListener('wheel', cancelScrollForInput);
  }, []);

  useEffect(() => {
    if (!watchCustomAmount) {
      trigger('amount');
      return;
    }

    setValue('amount', '');
  }, [watchCustomAmount]);

  useEffect(() => {
    if (!watchAmount) {
      return;
    }

    setValue('customAmount', '');
    trigger('amount', 'customAmount');
  }, [watchAmount]);

  useEffect(() => {
    setValue('amount', activeVariant ? getValue(activeVariant?.price) : '');
  }, []);

  const setAutoHeight = () => {
    textareaRef.current.style.height = '';
    textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
  };

  useEffect(() => {
    if (textareaRef.current) {
      setAutoHeight();
    }
  }, [textareaRef, watchMessageToRecipient]);

  const onSubmit = (data) => {
    const formatData = {
      productId: productId,
      value: Number(data.amount.slice(1)) || Number(data.customAmount),
      senderName: data.yourName,
      receiverName: data.recipientName,
      email: data.recipientEmail,
      message: data.messageToRecipient || '',
    };

    setLoading(true);

    handleAddToCart(formatData)
      .then((data) => {
        setLoading(false);
        if (!data) return;
        setCartData({ cart: data });
      })
      .catch(() => setLoading(false));
  };

  const validationMessage = () => {
    if (errors?.amount?.type === 'required' && errors?.customAmount?.type === 'required') {
      return 'Custom Amount or amount is required';
    } else if (errors?.customAmount?.type === 'validationAmount') {
      return errors?.customAmount?.message;
    }
  };

  return (
    <div className={styles.createAccountContainer}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <TextSwatches
          className={styles.textSwatches}
          activeRadio={watchAmount}
          setValue={setValue}
          {...register('amount', {
            onChange: (e) => {
              const actualVariant = variants.find((el) => Number(e.value.slice(1)) * 100 === el.price);
              setActiveVariant(actualVariant);
              setValue('amount', e.value);
              clearErrors('customAmount');
            },
          })}
          activeVariant={activeVariant}
          setActiveVariant={setActiveVariant}
          variants={variants}
          getValue={getValue}
        />

        <TextField
          className={styles.textField}
          customClasses={{
            inputGeneral: iconStatus || watchCustomAmount ? styles.extraPadding : styles.customInputStyles,
          }}
          borderColor={iconStatus || watchCustomAmount ? '#000000' : '#D3D6D9'}
          onFocus={() => {
            if (watchAmount) {
              clearErrors('customAmount');
            }
            setIconStatus(true);
          }}
          type="number"
          placeholder="Enter Custom Amount"
          variant="standard"
          {...register('customAmount', {
            onBlur: () => {
              if (watchAmount) {
                clearErrors('customAmount');
              }
              setIconStatus(false);
            },
          })}
          errorMessage={validationMessage(errors)}
          error={errors?.customAmount?.message}
          inputMode="numeric"
          iconBefore={(iconStatus || watchCustomAmount) && <div className={styles.iconDollar}>$</div>}
        />
        {options.theme === 'Jag' && (
          <Heading as="h2" textTransform="uppercase" className={clsx(textSwatchesStyles.titleAmount, styles.titleHeading)}>
            Details
          </Heading>
        )}
        <div className={styles.formBlock}>
          <TextField
            className={styles.textField}
            customClasses={{ inputGeneral: styles.inputStyles }}
            borderColor="#D3D6D9"
            type="text"
            placeholder="Your Name*"
            variant="standard"
            {...register('yourName')}
            errorMessage={errors?.yourName?.message}
            error={!!errors?.yourName}
          />
          <TextField
            className={styles.textField}
            customClasses={{ inputGeneral: styles.inputStyles }}
            borderColor="#D3D6D9"
            type="text"
            variant="standard"
            placeholder="Recipient Name*"
            {...register('recipientName')}
            errorMessage={errors?.recipientName?.message}
            error={!!errors?.recipientName}
          />
          <TextField
            className={styles.textField}
            customClasses={{ inputGeneral: styles.inputStyles }}
            borderColor="#D3D6D9"
            type="email"
            variant="standard"
            placeholder="Recipient Email*"
            {...register('recipientEmail')}
            errorMessage={errors?.recipientEmail?.message}
            error={!!errors?.recipientEmail}
          />
          <TextField
            className={styles.textField}
            customClasses={options.customClasses}
            borderColor="#D3D6D9"
            variant="standard"
            multiline
            placeholder="Message to Recipient"
            {...register('messageToRecipient')}
            textareaRef={textareaRef}
            onKeyDown={setAutoHeight}
          />
        </div>
        <Button disabled={isLoading} className={styles.btnLogin} type="submit">
          {isLoading ? <PrimaryLoader mode="segment" theme="light" size={20} /> : 'Add to Cart'}
        </Button>
      </form>
    </div>
  );
};

export default GiftCardForm;
